/**
 * CustomFieldFormController
 *
 * Manages the custom field form behavior including:
 * - Showing/hiding dropdown options based on field type
 * - Showing/hiding conditional logic section based on association model
 * - Fetching and populating available fields for parent field dropdowns
 *
 * DOM dependencies:
 *   - Input/select with data-custom-field-form-target="fieldType"
 *   - Input/select with data-custom-field-form-target="associationModel"
 *   - Div#dropdown-container (hidden when field type is not "dropdown")
 *   - Div#conditions-section (hidden except for on_enrolment/on_enrolment_item)
 *   - Div#conditions-fields containing nested condition rows
 *
 * Data attributes:
 *   - data-custom-field-form-url: URL to fetch available fields from
 *
 * Actions:
 *   - fieldTypeChanged: triggered on field type select change
 *   - associationModelChanged: triggered on association model select change
 *   - removeCondition: triggered on condition remove button click
 *   - nested:fieldAdded (jQuery event): listened on connect() to populate new condition rows
 */
import { Controller } from "@hotwired/stimulus";
import Rails from "@rails/ujs";

export default class extends Controller {
  static targets = ["fieldType", "associationModel"];

  static values = {
    availableFields: { type: Array, default: [] }
  };

  connect() {
    this._updateDropdownVisibility();
    this._updateConditionsVisibility();

    if (this.hasAssociationModelTarget) {
      this._loadAvailableFields();
    }

    // jquery_nested_form fires "nested:fieldAdded" after inserting the new row,
    // so we must listen here rather than on the click action (which fires before
    // the row is in the DOM).
    $(this.element).on("nested:fieldAdded.customFieldForm", () => {
      this._updateFieldsList();
    });
  }

  disconnect() {
    $(this.element).off("nested:fieldAdded.customFieldForm");
  }

  _loadAvailableFields() {
    const associationModel = this.associationModelTarget?.value || "";

    // Only load available fields for models that support conditional logic
    if (associationModel !== "on_enrolment" && associationModel !== "on_enrolment_item") {
      return;
    }

    const baseUrl = this.element.dataset.customFieldFormUrl;
    const params = new URLSearchParams({ association_model: associationModel });
    const url = `${baseUrl}?${params.toString()}`;

    Rails.ajax({
      url: url,
      type: "GET",
      dataType: "json",
      success: (fields) => {
        this.availableFieldsValue = fields;
        this._updateFieldsList();
      },
      error: () => {
        console.error("Failed to load available fields");
      }
    });
  }

  fieldTypeChanged(event) {
    this._updateDropdownVisibility();
  }

  associationModelChanged(event) {
    // Clear stale cached fields and reset all parent-field dropdowns immediately
    // before the new AJAX response arrives, so old options don't linger.
    this.availableFieldsValue = [];
    this._clearFieldSelects();
    this._loadAvailableFields();
    this._updateConditionsVisibility();
  }

  _updateDropdownVisibility() {
    if (!this.hasFieldTypeTarget) return;
    const type = this.fieldTypeTarget.value;
    const container = document.getElementById("dropdown-container");
    if (!container) return;

    if (type === "dropdown") {
      container.classList.remove("d-none");
      const select = document.getElementById("dropdown-options");
      if (select && !select.dataset.select2Initialized) {
        select.dataset.select2Initialized = "true";
        $(select).select2({ tags: true });
      }
    } else {
      container.classList.add("d-none");
    }
  }

  _updateConditionsVisibility() {
    if (!this.hasAssociationModelTarget) return;
    const model = this.associationModelTarget.value;
    const conditionsSection = document.getElementById("conditions-section");
    if (!conditionsSection) return;

    // Only show conditional logic for on_enrolment and on_enrolment_item
    if (model === "on_enrolment" || model === "on_enrolment_item") {
      conditionsSection.classList.remove("d-none");
    } else {
      conditionsSection.classList.add("d-none");
      // Clear any existing conditions when hiding
      const conditionRows = conditionsSection.querySelectorAll(".condition-row");
      conditionRows.forEach(row => {
        const checkbox = row.querySelector(".condition-destroy-checkbox");
        if (checkbox) {
          checkbox.checked = true;
        } else {
          row.remove();
        }
      });
    }
  }

  _clearFieldSelects() {
    const selects = document.querySelectorAll(".condition-row select[name*='[parent_field_id]']");
    selects.forEach((select) => {
      select.innerHTML = '<option value="">Select Field</option>';
    });
  }

  _updateFieldsList() {
    // Don't update if we have no fields loaded (empty means nothing available or still loading)
    if (!this.availableFieldsValue || this.availableFieldsValue.length === 0) {
      this._clearFieldSelects();
      return;
    }
    if (!this.hasAssociationModelTarget) return;

    const currentModel = this.associationModelTarget.value;

    // If no association model selected, show all fields; otherwise filter
    let filteredFields;
    if (!currentModel || currentModel === "") {
      filteredFields = this.availableFieldsValue;
    } else {
      filteredFields = this.availableFieldsValue.filter(
        (f) => f.association_model === currentModel
      );
    }

    // Update all parent field selects in conditions section
    const conditionRows = document.querySelectorAll(".condition-row select[name*='[parent_field_id]']");
    conditionRows.forEach((select) => {
      const currentValue = select.value;
      select.innerHTML = '<option value="">Select Field</option>';
      filteredFields.forEach((f) => {
        const option = document.createElement("option");
        option.value = f.id;
        option.textContent = f.field_name;
        select.appendChild(option);
      });
      if (currentValue) select.value = currentValue;
    });
  }

  removeCondition(event) {
    event.preventDefault();
    const row = event.target.closest(".condition-row");
    if (!row) return;

    const checkbox = row.querySelector(".condition-destroy-checkbox");
    if (checkbox) {
      if (confirm("Are you sure you want to remove this condition?")) {
        checkbox.checked = true;
        row.classList.add("opacity-50");
        row.querySelectorAll("input, select").forEach((el) => {
          el.disabled = true;
        });
      }
    } else {
      row.remove();
    }
  }
}
